import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Check, ArrowRight, Waves, Home, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * LocationLandingPage — a content-heavy SEO landing page template.
 * Each instance receives fully unique long-form copy (800+ words) so Google
 * treats every page as a distinct local result and not duplicate content.
 */
const LocationLandingPage = ({
  slug,
  title,
  metaDescription,
  h1,
  subhead,
  heroImage,
  keywords,
  primaryLocation,
  intro,        // array of 2-3 paragraphs (unique)
  whyLive,      // array of 4-6 "why live here" bullets
  distances,    // [{label, value}]
  neighborhoods,// array of 3-5 short items with title + body
  faq,          // [{q, a}]
  closing,      // final paragraph
}) => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    name: `Paradise Beach Rental — ${primaryLocation}`,
    description: metaDescription,
    url: `https://2161toddlane.com${slug}`,
    telephone: "+1-407-450-9182",
    email: "RAEVenturesLLC@gmail.com",
    priceRange: "$4,250 - $4,500 per month",
    image: heroImage,
    numberOfBedrooms: 4,
    numberOfBathrooms: 3,
    occupancy: { "@type": "QuantitativeValue", maxValue: 10 },
    petsAllowed: true,
    address: {
      "@type": "PostalAddress",
      streetAddress: "2161 Todd Ln",
      addressLocality: "Indialantic",
      addressRegion: "FL",
      postalCode: "32903",
      addressCountry: "US",
    },
    geo: { "@type": "GeoCoordinates", latitude: 28.0883, longitude: -80.5667 },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "12" },
    areaServed: primaryLocation,
  };

  const faqLd = faq && faq.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  const combinedLd = faqLd ? { "@graph": [jsonLd, faqLd] } : jsonLd;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid={`location-landing-${slug.replace(/\//g, "")}`}>
      <SEO
        title={title}
        description={metaDescription}
        path={slug}
        image={heroImage}
        keywords={keywords}
        jsonLd={combinedLd}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: primaryLocation, path: slug },
        ]}
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="relative min-h-[70vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img src={heroImage} alt={`${primaryLocation} luxury beach rental exterior`} className="w-full h-full object-cover" fetchPriority="high" />
            <div className="absolute inset-0 bg-[#0A1628]/70" />
          </div>
          <div className="relative z-10 container-premium pb-20 pt-40">
            <div className="gold-line mb-6" />
            <span className="label-sm text-[#C9A962]">{primaryLocation}, Florida</span>
            <h1 className="headline-xl text-white mt-4 mb-6 max-w-3xl">{h1}</h1>
            <p className="body-lg text-white/80 max-w-2xl mb-8">{subhead}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-6 text-base">
                  Check Availability
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:4074509182">
                <Button className="bg-transparent border border-white/40 text-white hover:bg-white/10 rounded-none px-8 py-6 text-base">
                  Call (407) 450-9182
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Intro (long-form content — critical for ranking) */}
        <section className="section-lg">
          <div className="container-premium max-w-4xl">
            <div className="gold-line mb-6" />
            <h2 className="headline-lg text-[#0A1628] mb-8">
              Extended-Stay Rentals in {primaryLocation}
            </h2>
            <div className="prose prose-lg max-w-none space-y-6">
              {intro.map((p, i) => (
                <p key={i} className="body-md text-[#0A1628]/75 leading-relaxed text-lg">{p}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Why Live Here */}
        <section className="section-lg bg-[#F7F3ED]">
          <div className="container-premium">
            <div className="max-w-4xl mx-auto">
              <span className="label-sm text-[#C9A962]">Why {primaryLocation}</span>
              <h2 className="headline-lg text-[#0A1628] mt-3 mb-10">
                Everything You Need Within Minutes
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {whyLive.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex gap-4 bg-white p-6"
                  >
                    <Check className="w-5 h-5 text-[#C9A962] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-body font-semibold text-[#0A1628] mb-1">{item.title}</h3>
                      <p className="text-sm text-[#0A1628]/70 leading-relaxed">{item.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Distances */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="headline-md text-[#0A1628] mb-10">Minutes from Everything</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {distances.map((d, i) => (
                  <div key={i} className="bg-[#0A1628] p-8 text-center">
                    <p className="font-display text-3xl text-[#C9A962] mb-2">{d.value}</p>
                    <p className="text-xs uppercase tracking-wider text-white/70">{d.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Neighborhoods / What to Know */}
        <section className="section-lg bg-[#F7F3ED]">
          <div className="container-premium max-w-4xl">
            <h2 className="headline-lg text-[#0A1628] mb-10">What to Know Before You Stay</h2>
            <div className="space-y-8">
              {neighborhoods.map((n, i) => (
                <article key={i} className="border-l-2 border-[#C9A962] pl-6">
                  <h3 className="font-display text-2xl text-[#0A1628] mb-3">{n.title}</h3>
                  <p className="body-md text-[#0A1628]/70 leading-relaxed">{n.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        {faq && faq.length > 0 && (
          <section className="section-lg">
            <div className="container-premium max-w-4xl">
              <h2 className="headline-lg text-[#0A1628] mb-10">
                Common Questions About {primaryLocation} Rentals
              </h2>
              <div className="space-y-6">
                {faq.map((f, i) => (
                  <div key={i} className="border-b border-[#0A1628]/10 pb-6">
                    <h3 className="font-body font-semibold text-[#0A1628] text-lg mb-2">{f.q}</h3>
                    <p className="body-md text-[#0A1628]/70 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Closing content block */}
        <section className="py-16 bg-[#FDFBF7]">
          <div className="container-premium max-w-4xl">
            <p className="body-md text-[#0A1628]/70 leading-relaxed text-lg italic">{closing}</p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container-premium text-center">
            <div className="gold-line mx-auto mb-6" />
            <h2 className="headline-lg text-white mb-4">
              Ready to book your {primaryLocation} stay?
            </h2>
            <p className="body-md text-white/60 mb-8 max-w-xl mx-auto">
              Direct-owner booking, no third-party fees. 90-day minimum. Availability fills fast for peak months.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D] rounded-none px-8 py-6 text-base">
                  Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:4074509182">
                <Button className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-none px-8 py-6 text-base">
                  Call (407) 450-9182
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />
    </div>
  );
};

export default LocationLandingPage;
